'use client';

import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';

interface VideoPlayerProps {
    videoUrl: string;
    title?: string;
    autoPlay?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, title, autoPlay = false }) => {
    const [embedUrl, setEmbedUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isDirectVideo, setIsDirectVideo] = useState(false);

    useEffect(() => {
        if (!videoUrl) {
            setIsLoading(false);
            return;
        }

        try {
            // Handle blob URLs (temporary object URLs)
            if (videoUrl.startsWith('blob:')) {
                setIsDirectVideo(true);
                setEmbedUrl(videoUrl);
                setIsLoading(false);
                return;
            }

            // Process YouTube URLs
            if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
                let videoId = '';

                if (videoUrl.includes('youtube.com/watch')) {
                    const url = new URL(videoUrl);
                    videoId = url.searchParams.get('v') || '';
                } else if (videoUrl.includes('youtu.be/')) {
                    videoId = videoUrl.split('youtu.be/')[1].split('?')[0];
                } else if (videoUrl.includes('youtube.com/embed/')) {
                    videoId = videoUrl.split('youtube.com/embed/')[1].split('?')[0];
                }

                if (videoId) {
                    // Add autoplay parameter if requested
                    setEmbedUrl(`https://www.youtube.com/embed/${videoId}${autoPlay ? '?autoplay=1' : ''}`);
                } else {
                    setError('Invalid YouTube URL');
                }
            }
            // Process Vimeo URLs
            else if (videoUrl.includes('vimeo.com')) {
                const vimeoId = videoUrl.split('vimeo.com/')[1].split('?')[0].split('#')[0];
                if (vimeoId) {
                    // Add autoplay parameter if requested
                    setEmbedUrl(`https://player.vimeo.com/video/${vimeoId}${autoPlay ? '?autoplay=1' : ''}`);
                } else {
                    setError('Invalid Vimeo URL');
                }
            }
            // Handle direct embed URLs
            else if (videoUrl.includes('embed') || videoUrl.includes('player')) {
                // Add autoplay parameter if the URL doesn't already have parameters
                if (autoPlay && !videoUrl.includes('?')) {
                    setEmbedUrl(`${videoUrl}?autoplay=1`);
                } else if (autoPlay && videoUrl.includes('?')) {
                    setEmbedUrl(`${videoUrl}&autoplay=1`);
                } else {
                    setEmbedUrl(videoUrl);
                }
            }
            // Direct video file URLs (.mp4, .webm, etc.)
            else if (/\.(mp4|webm|ogg|mov)$/i.test(videoUrl) || videoUrl.includes('storage.googleapis.com')) {
                setIsDirectVideo(true);
                setEmbedUrl(videoUrl);
            } else {
                // Try to load as direct video URL anyway as fallback
                setIsDirectVideo(true);
                setEmbedUrl(videoUrl);
                console.warn('Video URL format not explicitly recognized, trying as direct video:', videoUrl);
            }
        } catch (err) {
            console.error('Error processing video URL:', err, videoUrl);
            setError('Error processing video URL');
        }

        setIsLoading(false);
    }, [videoUrl, autoPlay]);

    // Function to try playing the video directly even if format is unknown
    const tryDirectVideo = () => {
        setError(null);
        setIsDirectVideo(true);
        setEmbedUrl(videoUrl);
    };

    if (!videoUrl) {
        return null;
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{
                p: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 1,
            }}>
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Box
                            component="button"
                            onClick={tryDirectVideo}
                            sx={{
                                background: 'none',
                                border: '1px solid white',
                                color: 'white',
                                p: 1,
                                borderRadius: 1,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                            }}
                        >
                            Try playing anyway
                        </Box>
                    }
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    // For direct video files
    if (isDirectVideo) {
        return (
            <Box sx={{ width: '100%', position: 'relative', pt: '56.25%' }}>
                {title && (
                    <Typography variant="h6" sx={{ mb: 1, color: 'white' }}>
                        {title}
                    </Typography>
                )}
                <video
                    controls
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    playsInline
                    preload="metadata"
                    autoPlay={autoPlay}
                    onError={(e) => {
                        console.error('Video playback error:', e);
                        setError('Unable to play this video. The URL may be invalid or temporary.');
                        setIsDirectVideo(false);
                    }}
                >
                    <source src={embedUrl || videoUrl} />
                    Your browser does not support the video tag.
                </video>
            </Box>
        );
    }

    // For embedded videos
    return (
        <Box sx={{ width: '100%', position: 'relative', pt: '56.25%' }}>
            {title && (
                <Typography variant="h6" sx={{ mb: 1, color: 'white' }}>
                    {title}
                </Typography>
            )}
            {embedUrl && (
                <iframe
                    src={embedUrl}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={title || "Venue Video"}
                />
            )}
        </Box>
    );
};

export default VideoPlayer; 